import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshStatus } = useSubscription();
  const [status, setStatus] = useState('verifying');
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid' && res.data.status === 'complete') {
          setPlan(res.data.plan);
          setStatus('success');
          await refreshStatus();
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        setStatus('error');
      }
    };

    poll();
  }, [sessionId, refreshStatus]);

  const planLabel = plan === 'premium' ? 'Premium' : plan === 'share' ? 'Share' : 'Standard';

  return (
    <div
      className="min-h-dvh flex items-center justify-center px-4"
      style={{ background: 'var(--theme-background)' }}
      data-testid="payment-success-page"
    >
      <div
        className="w-full max-w-sm rounded-3xl p-8 text-center"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--glass-border)',
        }}
      >
        {status === 'verifying' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
              {t('payment.processing')}
            </h2>
          </>
        )}

        {status === 'success' && (
          <>
            <div
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
              style={{ background: 'rgba(34,197,94,0.15)' }}
            >
              <CheckCircle className="h-9 w-9 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
              {t('payment.success_title')}
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('payment.success_desc')}
            </p>
            <Button
              onClick={() => navigate('/app')}
              className="w-full rounded-xl py-5 font-medium"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid="payment-success-continue-btn"
            >
              {t('payment.success_cta')}
            </Button>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
              {t('payment.cancel_title')}
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('payment.cancel_desc')}
            </p>
            <Button
              onClick={() => navigate('/app')}
              className="w-full rounded-xl py-5 font-medium"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid="payment-error-continue-btn"
            >
              {t('payment.cancel_cta')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
